// 時刻トリガー スケジューラ
// ブラウザが開いている間、1秒ごとに登録タスクをチェックして発火させる

import { AnyTask, Lv1Task, Lv2Task, Lv3Task } from '../types';

export type FireEvent = {
  task: AnyTask;
  /** Lv2/Lv3のとき: 'pre30' | 'pre15' | 'pre5' | 'onTime' */
  stage?: 'pre30' | 'pre15' | 'pre5' | 'onTime';
};

export type FireHandler = (e: FireEvent) => Promise<void> | void;

interface Schedule {
  taskId: string;
  /** 当日の発火時刻（Unix ms） */
  fireAt: number;
  stage?: 'pre30' | 'pre15' | 'pre5' | 'onTime';
}

export class Scheduler {
  private timer: number | null = null;
  /** 当日内ですでに発火済みのキー（taskId+stage+date） */
  private firedKeys = new Set<string>();
  /** 現在の日付（YYYY-MM-DD） 日付変わったらリセット */
  private currentDate = '';
  private getTasks: () => AnyTask[];
  private onFire: FireHandler;

  constructor(getTasks: () => AnyTask[], onFire: FireHandler) {
    this.getTasks = getTasks;
    this.onFire = onFire;
    this.currentDate = todayKey();
  }

  start(): void {
    if (this.timer !== null) return;
    this.tick(); // 即チェック
    this.timer = window.setInterval(() => this.tick(), 1000);
  }

  stop(): void {
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  /** 手動リセット（テスト用 or 設定変更時） */
  resetFiredKeys(): void {
    this.firedKeys.clear();
  }

  private tick(): void {
    const now = Date.now();
    const today = todayKey();
    if (today !== this.currentDate) {
      // 日付変わった → fired リセット
      this.firedKeys.clear();
      this.currentDate = today;
    }

    const tasks = this.getTasks().filter((t) => t.enabled);
    const schedules = computeTodaySchedules(tasks);

    for (const sch of schedules) {
      const key = `${sch.taskId}:${sch.stage ?? 'lv1'}:${this.currentDate}`;
      if (this.firedKeys.has(key)) continue;
      // 発火時刻を過ぎていて、なおかつ「過ぎすぎ」ていない（60秒以内）
      if (sch.fireAt <= now && now - sch.fireAt < 60_000) {
        this.firedKeys.add(key);
        const task = tasks.find((t) => t.id === sch.taskId);
        if (task) {
          Promise.resolve(this.onFire({ task, stage: sch.stage })).catch((err) => {
            console.error('[Scheduler] fire handler error', err);
          });
        }
      } else if (now - sch.fireAt >= 60_000) {
        // 1分以上過ぎたら「取りこぼし」として fired 扱い（鳴らさない）
        this.firedKeys.add(key);
      }
    }
  }
}

// ===== ヘルパー =====

function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

/** HH:mm → 当日のUnix ms */
function timeToToday(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.getTime();
}

/** 当日有効な発火スケジュールを計算 */
export function computeTodaySchedules(tasks: AnyTask[]): Schedule[] {
  const out: Schedule[] = [];
  const dow = new Date().getDay();
  for (const t of tasks) {
    if (t.daysOfWeek.length > 0 && !t.daysOfWeek.includes(dow)) continue;
    if (t.level === 'lv1') {
      const lv1 = t as Lv1Task;
      out.push({ taskId: t.id, fireAt: timeToToday(lv1.time) });
    } else {
      const lv2or3 = t as Lv2Task | Lv3Task;
      const eventTime = timeToToday(lv2or3.eventTime);
      out.push({ taskId: t.id, fireAt: eventTime - 30 * 60_000, stage: 'pre30' });
      out.push({ taskId: t.id, fireAt: eventTime - 15 * 60_000, stage: 'pre15' });
      out.push({ taskId: t.id, fireAt: eventTime - 5 * 60_000, stage: 'pre5' });
      out.push({ taskId: t.id, fireAt: eventTime, stage: 'onTime' });
    }
  }
  return out;
}
